<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { api, type SignalData, type CalendarStatus, type UserExecutionStatus,
  type UserExecutionConfig, type EnrichedSynthNavData, type VixIntraday,
  type UserOtpConfig, type SentimentHistoryPoint } from '../api/client'
import { useAuth } from '../composables/useAuth'
import { useSignalPipeline } from '../composables/useSignalPipeline'
import NewsSentimentCard from './NewsSentimentCard.vue'
import SignalVerdict from './signal/SignalVerdict.vue'
import SignalPipeline from './signal/SignalPipeline.vue'
import AnomalyRail from './signal/AnomalyRail.vue'
import MomentumGateCard from './signal/MomentumGateCard.vue'
import VixRegimeCard from './signal/VixRegimeCard.vue'
import NavSourceCard from './signal/NavSourceCard.vue'
import MarketContextCard from './signal/MarketContextCard.vue'
import OtpCard from './signal/OtpCard.vue'
import FuturesRuleCard from './signal/FuturesRuleCard.vue'
import RetiredDiagnosticsStrip from './signal/RetiredDiagnosticsStrip.vue'
import ExplainerDrawer from './signal/ExplainerDrawer.vue'

const { isAuthenticated, goToLogin } = useAuth()

const props = defineProps<{
  sharedSignal: SignalData | null
  sharedEnrichedNav: EnrichedSynthNavData | null
  activeFundReturnPct: number | null
}>()

// --- Refs fed straight through to the composable ---
const signal = computed<SignalData | null>(() => props.sharedSignal)
const enrichedNav = computed(() => props.sharedEnrichedNav)
const activeFundReturnPct = computed(() => props.activeFundReturnPct)
const calendar = ref<CalendarStatus | null>(null)
const execStatus = ref<UserExecutionStatus | null>(null)
const execConfig = ref<UserExecutionConfig | null>(null)
const vixIntraday = ref<VixIntraday | null>(null)
const otpConfigs = ref<UserOtpConfig[]>([])
const systemAlerts = ref<Array<{ type: string; message: string }>>([])
const sentimentHistory = ref<SentimentHistoryPoint[]>([])
const tick = ref(0)
const loading = ref(true)

const pipeline = useSignalPipeline({
  signal, enrichedNav, activeFundReturnPct,
  calendar, execStatus, execConfig, vixIntraday, otpConfigs, systemAlerts, tick,
})

// --- Inline explainer state (2026-07-27 UX revert: old popup semantics —
// an (i) click toggles its panel open/closed in place; a different (i)
// switches the panel to that section) ---
const explainerSection = ref<string | null>(null)
function openExplainer(key: string) {
  explainerSection.value = explainerSection.value === key ? null : key
}

// --- Glue computeds that assemble component props from composable outputs ---
const anchorDateLabel = computed<string | null>(() => {
  const e = enrichedNav.value
  if (e?.today_official_nav_date) return 'today ' + e.today_official_nav_date
  return e?.prev_official_nav_date ?? null
})

const noTrade = computed(() => {
  const c = calendar.value
  if (!c || c.tradingDay) return null
  return { reason: c.reason ?? '', weekend: c.weekend, nextTradingDay: c.nextTradingDay ?? null }
})

const xdBanner = computed(() => {
  // XD misprice mechanics are a BGA income-class phenomenon — never show the
  // banner on other funds' views (e.g. JH) even when the calendar carries the event.
  if (signal.value?.fund !== 'BGA') return null
  const evt = pipeline.xdEvent.value
  return evt ? { plusOne: pipeline.isXdPlusOne.value, active: evt.active } : null
})

const verdictProps = computed(() => ({
  fund: signal.value?.fund ?? null,
  decisionLabel: pipeline.decisionLabel.value,
  decision: pipeline.decision.value,
  decisionSubtitle: pipeline.decisionSubtitle.value,
  synthNavPence: pipeline.synthNavPence.value,
  overnightReturnPct: pipeline.overnightReturnPct.value,
  anchorPence: pipeline.anchorPence.value,
  anchorDateLabel: anchorDateLabel.value,
  countdown: pipeline.countdown.value,
  noTrade: noTrade.value,
  xdBanner: xdBanner.value,
  // Needle source is mode-aware: NQ overnight move under the futures rule,
  // the synth-NAV band signal otherwise (see useSignalPipeline.needleInput).
  rawSignal: pipeline.needleInput.value.value,
  needleScale: pipeline.needleInput.value.scale,
  provisional: pipeline.isProvisional.value,
  statePill: pipeline.stepAsideStatus.value,
}))

// --- Diagnostics grid (mode-driven) ---
// Card props are assembled once so the pure-mode and legacy branches of the
// template bind the same objects; the only difference between the branches
// is WHICH cards render where.
const momentumGateProps = computed(() => {
  const b = pipeline.bgaSignal.value
  if (!b) return null
  return { breakdown: pipeline.momentumGateBreakdown.value, wz: b.imr_wz ?? null,
    window: b.imr_window ?? null, stale: pipeline.signalStaleAsOf.value }
})
const vixRegimeProps = computed(() => ({
  vix: signal.value?.vix ?? null, regimeLabel: pipeline.regimeLabel.value,
  termStructure: signal.value?.vix_term_structure ?? null,
}))
const navSourceProps = computed(() => ({
  source: pipeline.bgaSignal.value?.synth_nav_source ?? null,
  ktReturnPct: pipeline.ktReturnPct.value, basketReturnPct: pipeline.basketReturnPct.value,
  anchorPence: pipeline.anchorPence.value, anchorDateLabel: anchorDateLabel.value,
}))
const marketContextProps = computed(() => ({
  signal: signal.value!, nas100Return: pipeline.nas100Return.value,
  nas100MarketReturn: pipeline.nas100MarketReturn.value, gbpusdReturn: pipeline.gbpusdReturn.value,
  gbpusdMarketReturn: pipeline.gbpusdMarketReturn.value, gbpusdRefDegraded: pipeline.gbpusdRefDegraded.value,
  fxWindPill: pipeline.fxWindPill.value,
}))
const otpProps = computed(() => ({
  otpConfigs: otpConfigs.value, otpBar: pipeline.otpBar, otpByType: pipeline.otpByType,
  otpDayType: pipeline.otpDayType.value, otpDivergence: pipeline.otpDivergence.value,
  otpExemptActive: pipeline.otpExemptActive.value, vixDelta: pipeline.vixDelta.value,
}))
// Under the futures rule the synth-NAV diagnostics collapse into one strip;
// the list names only the cards the payload can actually render.
const retiredItems = computed(() => {
  const items: string[] = []
  if (momentumGateProps.value) items.push('Momentum gate')
  items.push('VIX regime')
  if (navSourceProps.value.source) items.push('NAV source')
  return items
})

const narratives = computed(() => ({
  signal: pipeline.signalNarrative.value,
  imr: pipeline.imrNarrative.value,
  fva: pipeline.fvaNarrative.value,
}))

// --- Polling (SignalPanel.vue lines 703-749, preserved verbatim) ---
let timer: ReturnType<typeof setInterval> | null = null
let tickTimer: ReturnType<typeof setInterval> | null = null
let sentimentHistoryTimer: ReturnType<typeof setInterval> | null = null

async function poll() {
  // signal + enrichedNav come from Dashboard props (shared single poll)
  loading.value = false

  try {
    const es = await api.userExecutionStatus()
    execStatus.value = es
  } catch {
    execStatus.value = null
  }

  try {
    execConfig.value = await api.userExecutionConfig()
  } catch {
    // leave previous value in place
  }

  try {
    const vixData = await api.vixIntraday()
    vixIntraday.value = (vixData as any)?.available === false ? null : vixData
  } catch { vixIntraday.value = null }

  try { otpConfigs.value = await api.userOtpConfig() } catch {}
}

onMounted(async () => {
  poll()
  timer = setInterval(poll, 5000)
  tickTimer = setInterval(() => { tick.value++ }, 1000)

  api.calendarToday().then(c => { calendar.value = c }).catch(() => {})
  api.alerts().then(r => { systemAlerts.value = r.alerts ?? [] }).catch(() => {})
  // 30-day sentiment history for the NewsSentimentCard mini-spark + gauge
  // context. Refreshed once an hour — daily aggregator only updates the row
  // at 09:00 UTC, sub-daily polling adds nothing.
  api.chartSentimentHistory('30d').then(r => { sentimentHistory.value = r.points ?? [] }).catch(() => {})
  sentimentHistoryTimer = setInterval(() => {
    api.chartSentimentHistory('30d').then(r => { sentimentHistory.value = r.points ?? [] }).catch(() => {})
  }, 60 * 60 * 1000)
})

onUnmounted(() => {
  if (timer) clearInterval(timer)
  if (tickTimer) clearInterval(tickTimer)
  if (sentimentHistoryTimer) clearInterval(sentimentHistoryTimer)
})
</script>

<template>
  <section class="sp-card">
    <!-- Unauthenticated overlay — placeholder SVG here is a static asset, NOT
         the retired binary gauge; kept verbatim so anonymous visitors see a
         non-broken preview of the signal card. -->
    <div v-if="!isAuthenticated" class="unauth-overlay">
      <div style="width:82px;margin:0 auto 0.5rem;">
        <svg viewBox="0 0 100 68" class="binary-gauge" style="opacity:0.35;">
          <path d="M 10 50 A 40 40 0 0 1 90 50" fill="none" stroke="#21262d" stroke-width="10" stroke-linecap="round"/>
          <path d="M 10 50 A 40 40 0 0 1 50 10" fill="none" stroke="#484f58" stroke-width="10" stroke-linecap="round"/>
          <path d="M 50 10 A 40 40 0 0 1 90 50" fill="none" stroke="#484f58" stroke-width="10" stroke-linecap="round"/>
          <circle cx="50" cy="50" r="4" fill="#484f58"/>
        </svg>
      </div>
      <div style="font-size:2rem;font-weight:700;color:#484f58;">N/A</div>
      <div style="font-size:0.9rem;color:#484f58;margin-bottom:1.5rem;">Log in to view signal</div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:1rem;">
        <div style="background:#0d1117;border-left:3px solid #30363d;border-radius:4px;padding:6px 8px;">
          <div style="font-size:0.65rem;color:#484f58;text-transform:uppercase;">Signal</div>
          <div style="font-size:1.1rem;font-weight:700;color:#484f58;font-family:monospace;">N/A</div>
          <a style="font-size:0.75rem;color:#58a6ff;cursor:pointer;text-decoration:underline;" @click="goToLogin">Log in to view &rarr;</a>
        </div>
        <div style="background:#0d1117;border-left:3px solid #30363d;border-radius:4px;padding:6px 8px;">
          <div style="font-size:0.65rem;color:#484f58;text-transform:uppercase;">IMR Momentum</div>
          <div style="font-size:1.1rem;font-weight:700;color:#484f58;font-family:monospace;">N/A</div>
          <a style="font-size:0.75rem;color:#58a6ff;cursor:pointer;text-decoration:underline;" @click="goToLogin">Log in to view &rarr;</a>
        </div>
      </div>
      <div style="padding:0.75rem;background:#0d1117;border:1px solid #30363d;border-radius:4px;text-align:center;">
        <div style="color:#8b949e;font-size:0.85rem;margin-bottom:0.5rem;">Sign in to access live trading signals</div>
        <a style="display:inline-block;padding:6px 16px;background:#21262d;border:1px solid #58a6ff;border-radius:4px;color:#58a6ff;font-size:0.85rem;font-weight:600;cursor:pointer;" @click="goToLogin">Go to Account &rarr;</a>
      </div>
    </div>

    <template v-else>
      <div v-if="loading" class="sp-loading">Loading signal data...</div>
      <div v-else-if="!signal" class="sp-unavailable">Signal service not connected</div>
      <template v-else>
        <SignalVerdict v-bind="verdictProps" @explain-nav="openExplainer('nav')" />

        <SignalPipeline
          :stages="pipeline.stages.value"
          :decision="pipeline.decision.value"
          :provisional="pipeline.isProvisional.value"
          @explain="openExplainer" />

        <AnomalyRail :anomalies="pipeline.anomalies.value" />

        <!-- Inline info panel — sits between the pipeline/verdict icons above
             and the diagnostics-card icons below, so whichever (i) was
             clicked, its panel opens adjacent (pre-redesign popup UX). -->
        <ExplainerDrawer
          :open-section="explainerSection"
          :narratives="narratives"
          :signal="signal"
          :enriched-nav="enrichedNav"
          :xd-correction-pct="pipeline.xdCorrectionPct.value"
          :xd-correction-label="pipeline.xdCorrectionLabel.value"
          :momentum-gate-breakdown="pipeline.momentumGateBreakdown.value"
          :anchor-pence="pipeline.anchorPence.value"
          :basket-return-pct="pipeline.basketReturnPct.value"
          :step-aside="pipeline.stepAside.value" />

        <!-- Diagnostics grid — mode-driven. Futures rule (pure step-aside,
             BGA + JH): the rule card leads, synth-NAV cards collapse into one
             retired strip. Legacy synth-NAV presets: the original card set.
             Fund-specific cards (OTP, News) render only where the payload /
             user config has them, so no fund shows an empty card. -->
        <div class="diagnostics-grid">
          <template v-if="pipeline.isPureFuturesMode.value">
            <FuturesRuleCard :step-aside="pipeline.stepAside.value" :status="pipeline.stepAsideStatus.value" />
            <MarketContextCard v-bind="marketContextProps" />
            <OtpCard v-if="otpConfigs.length" v-bind="otpProps" />
            <div v-if="pipeline.bgaSignal.value?.news_basket_coverage_pct != null"
                 data-testid="news-sentiment-section">
              <NewsSentimentCard :signal="pipeline.bgaSignal.value" :history="sentimentHistory" />
            </div>
            <RetiredDiagnosticsStrip :items="retiredItems" class="grid-span">
              <MomentumGateCard v-if="momentumGateProps" v-bind="momentumGateProps" @explain="openExplainer" />
              <VixRegimeCard v-bind="vixRegimeProps" />
              <NavSourceCard v-bind="navSourceProps" />
            </RetiredDiagnosticsStrip>
          </template>
          <template v-else>
            <MomentumGateCard v-if="momentumGateProps" v-bind="momentumGateProps" @explain="openExplainer" />
            <VixRegimeCard v-bind="vixRegimeProps" />
            <NavSourceCard v-bind="navSourceProps" />
            <MarketContextCard v-bind="marketContextProps" />
            <OtpCard v-if="otpConfigs.length" v-bind="otpProps" />
            <div v-if="pipeline.bgaSignal.value?.news_basket_coverage_pct != null"
                 data-testid="news-sentiment-section">
              <NewsSentimentCard :signal="pipeline.bgaSignal.value" :history="sentimentHistory" />
            </div>
          </template>
        </div>

        <div v-if="signal.error" class="error-text">{{ signal.error }}</div>
      </template>
    </template>
  </section>
</template>

<style scoped>
.sp-card {
  background: #161b22;
  border: 1px solid #21262d;
  border-radius: 8px;
  padding: 12px;
  color: #c9d1d9;
  font-family: system-ui, -apple-system, sans-serif;
}

.unauth-overlay { padding: 1.5rem 1rem; text-align: center; }
.binary-gauge { width: 82px; height: 56px; display: block; }

.sp-loading { color: #6e7681; padding: 1rem 0; text-align: center; }
.sp-unavailable { color: #6e7681; font-style: italic; padding: 0.5rem 0; }

.diagnostics-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  margin-top: 6px;
}
.diagnostics-grid .grid-span { grid-column: 1 / -1; }
@media (max-width: 640px) {
  .diagnostics-grid { grid-template-columns: 1fr; }
}

.error-text { color: #f85149; font-size: 1.25rem; margin-top: 6px; }
</style>
